/** Small bottom pill, auto-dismissed. One at a time; a new toast replaces the current one. */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useLocation } from 'react-router-dom';
import { Icon } from './Icon';
import { hasTabbar } from './tabbarPaths';

const DURATION = 1900;

interface ToastState {
  message: string;
  tone: 'success' | 'error';
}

const ToastContext = createContext<(message: string, tone?: 'success' | 'error') => void>(() => {});

export function useToast() {
  return useContext(ToastContext);
}

export function ToastProvider({ children }: { children: React.ReactNode }) {
  const [toast, setToast] = useState<ToastState | null>(null);
  const timer = useRef<number>();
  const { pathname } = useLocation();

  const show = useCallback((message: string, tone: 'success' | 'error' = 'success') => {
    window.clearTimeout(timer.current);
    setToast({ message, tone });
    timer.current = window.setTimeout(() => setToast(null), DURATION);
  }, []);

  useEffect(() => () => window.clearTimeout(timer.current), []);

  const value = useMemo(() => show, [show]);

  return (
    <ToastContext.Provider value={value}>
      {children}
      {toast && (
        <div
          role="status"
          aria-live="polite"
          className="pointer-events-none fixed inset-x-0 z-[60] flex justify-center px-gutter"
          /* Only clear the tab bar on screens that have one. This reserved 56px everywhere, so a
             "Copied" on the detail screen — the most common toast in the product — sat a tab bar's
             height above the space it was meant to occupy. */
          style={{
            bottom: hasTabbar(pathname)
              ? 'calc(var(--pv-tabbar-height) + var(--pv-safe-bottom) + 32px)'
              : 'calc(var(--pv-safe-bottom) + 32px)',
          }}
        >
          <div className="pv-sheet-in flex max-w-[300px] items-center gap-2.5 rounded-button bg-toast-bg px-4 py-2.5 text-subhead text-toast-text">
            <Icon
              name={toast.tone === 'success' ? 'check' : 'close'}
              size={17}
              className={
                toast.tone === 'success' ? 'flex-none text-accent' : 'flex-none text-destructive'
              }
            />
            <span>{toast.message}</span>
          </div>
        </div>
      )}
    </ToastContext.Provider>
  );
}
